/**
 * @fileoverview Queue Redux slice.
 * @summary Manages song queue state (list, CRUD operations).
 *
 * @package KaraoNow
 * @module apps/front/src/store/slices/queueSlice
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'

/**
 * @summary Queue item status type.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export type QueueItemStatus = 'pending' | 'current' | 'completed' | 'cancelled'

/**
 * @summary Singer info in queue item.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export interface QueueSinger {
  id: string
  pseudo: string
  guestMode: boolean
}

/**
 * @summary Queue item interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export interface QueueItem {
  id: string
  songId: string
  songTitle: string
  songArtist: string
  position: number
  status: QueueItemStatus
  addedAt: string | Date
  playedAt?: string | Date
  singer: QueueSinger | null
}

/**
 * @summary Queue state interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface QueueState {
  items: QueueItem[]
  isLoading: boolean
  error: string | null
  currentEventId: string | null
}

/**
 * @summary Initial queue state.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const initialState: QueueState = {
  items: [],
  isLoading: false,
  error: null,
  currentEventId: null,
}

/**
 * @summary Fetch queue for an event.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const fetchQueue = createAsyncThunk('queue/fetchQueue', async (eventId: string) => {
  const response = await apiRequest<{ queue: QueueItem[]; count: number }>({
    method: 'GET',
    url: API_ENDPOINTS.QUEUE.GET(eventId),
  })

  if (!response.success) {
    throw new Error(response.message || 'Failed to fetch queue')
  }

  return { eventId, queue: response.data!.queue }
})

/**
 * @summary Add song to queue.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const addSongToQueue = createAsyncThunk(
  'queue/addSongToQueue',
  async (
    { eventId, songId, sessionToken }: { eventId: string; songId: string; sessionToken?: string },
    { rejectWithValue },
  ) => {
    const response = await apiRequest<any>({
      method: 'POST',
      url: API_ENDPOINTS.QUEUE.ADD(eventId),
      data: { songId, sessionToken },
    })

    if (!response.success) {
      // Extract additional error details if available
      let currentCount: number | undefined
      let limit: number | undefined
      
      if (response.data && typeof response.data === 'object') {
        // Check if data has the nested structure or is directly the values
        if ('currentCount' in response.data) {
          currentCount = Number(response.data.currentCount)
        }
        if ('limit' in response.data) {
          limit = Number(response.data.limit)
        }
      }
      
      // Use rejectWithValue to preserve error data
      return rejectWithValue({
        message: response.message || 'Failed to add song to queue',
        code: response.code,
        currentCount,
        limit,
      })
    }

    // Success case: response.data contains { queueItem }
    return { eventId, queueItem: response.data.queueItem }
  },
)

/**
 * @summary Update song position in queue.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const updateQueuePosition = createAsyncThunk(
  'queue/updatePosition',
  async ({ eventId, songId, position }: { eventId: string; songId: string; position: number }) => {
    const response = await apiRequest<{ queueItem: QueueItem }>({
      method: 'PUT',
      url: API_ENDPOINTS.QUEUE.UPDATE_POSITION(eventId, songId),
      data: { position },
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to update queue position')
    }

    return { eventId, queueItem: response.data!.queueItem }
  },
)

/**
 * @summary Update song status in queue.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const updateQueueStatus = createAsyncThunk(
  'queue/updateStatus',
  async ({ eventId, songId, status }: { eventId: string; songId: string; status: QueueItemStatus }) => {
    const response = await apiRequest<{ queueItem: QueueItem }>({
      method: 'PUT',
      url: API_ENDPOINTS.QUEUE.UPDATE_STATUS(eventId, songId),
      data: { status },
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to update queue status')
    }

    return { eventId, queueItem: response.data!.queueItem }
  },
)

/**
 * @summary Remove song from queue.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const removeSongFromQueue = createAsyncThunk(
  'queue/removeSongFromQueue',
  async ({ eventId, songId }: { eventId: string; songId: string }) => {
    const response = await apiRequest({
      method: 'DELETE',
      url: API_ENDPOINTS.QUEUE.DELETE(eventId, songId),
    })

    if (!response.success) {
      throw new Error(response.message || 'Failed to remove song from queue')
    }

    return { eventId, songId }
  },
)

/**
 * @summary Queue slice.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const queueSlice = createSlice({
  name: 'queue',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearQueue: (state) => {
      state.items = []
      state.currentEventId = null
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch queue
    builder
      .addCase(fetchQueue.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchQueue.fulfilled, (state, action) => {
        state.isLoading = false
        state.items = action.payload.queue
        state.currentEventId = action.payload.eventId
        state.error = null
      })
      .addCase(fetchQueue.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch queue'
      })

    // Add song to queue
    builder
      .addCase(addSongToQueue.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(addSongToQueue.fulfilled, (state, action) => {
        state.isLoading = false
        // Add new item to queue (will be sorted by position on next fetch)
        state.items.push(action.payload.queueItem)
        // Sort by position
        state.items.sort((a, b) => a.position - b.position)
        state.error = null
      })
      .addCase(addSongToQueue.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to add song to queue'
      })

    // Update position
    builder
      .addCase(updateQueuePosition.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateQueuePosition.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.items.findIndex((item) => item.id === action.payload.queueItem.id)
        if (index >= 0) {
          // Merge with existing item to preserve data not returned by API
          state.items[index] = {
            ...state.items[index],
            ...action.payload.queueItem,
            singer: action.payload.queueItem.singer ?? state.items[index].singer,
          }
          // Re-sort by position
          state.items.sort((a, b) => a.position - b.position)
        }
        state.error = null
      })
      .addCase(updateQueuePosition.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to update queue position'
      })

    // Update status
    builder
      .addCase(updateQueueStatus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateQueueStatus.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.items.findIndex((item) => item.id === action.payload.queueItem.id)
        if (index >= 0) {
          // Merge with existing item to preserve data not returned by API
          state.items[index] = {
            ...state.items[index],
            ...action.payload.queueItem,
            singer: action.payload.queueItem.singer ?? state.items[index].singer,
          }
        }
        state.error = null
      })
      .addCase(updateQueueStatus.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to update queue status'
      })

    // Remove song from queue
    builder
      .addCase(removeSongFromQueue.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(removeSongFromQueue.fulfilled, (state, action) => {
        state.isLoading = false
        state.items = state.items.filter((item) => item.id !== action.payload.songId)
        state.error = null
      })
      .addCase(removeSongFromQueue.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to remove song from queue'
      })
  },
})

export const { clearError, clearQueue } = queueSlice.actions
export default queueSlice.reducer

